/**
 * Ask Leo view modes and floating-window geometry.
 *
 * Leo has three shells: the docked **panel** (right rail), a floating
 * **window**, and **full screen** (`/<product>/leo`). Full screen is derived
 * from the URL — it is a route. Panel vs window is a stored user preference,
 * because it is a working-style choice that should survive a reload.
 *
 * The geometry maths lives here as pure functions so drag, resize, keyboard
 * nudges, and the on-mount re-clamp all agree on one definition of "legal
 * position". The re-clamp matters: geometry saved on a 32" monitor would
 * otherwise place the window off the edge of a laptop screen with no way to
 * drag it back.
 *
 * @see components/ask-leo-window.tsx — the only consumer of the rect helpers
 * @see docs/persisted-state-pattern.md — why these keys are shell-global
 */

/** Where the conversation renders. Full screen is a route, so it is not here. */
export type AskLeoDock = "panel" | "window"

/**
 * Shell-global, not product-namespaced: Leo is shell chrome, and a coordinator
 * who prefers a window in one product prefers it in all of them. Same reasoning
 * as `shell:ask-leo-panel-width`.
 */
export const ASK_LEO_DOCK_KEY = "shell:ask-leo-dock"
export const ASK_LEO_WINDOW_RECT_KEY = "shell:ask-leo-window-rect"

export interface AskLeoWindowRect {
  x: number
  y: number
  width: number
  height: number
}

export interface AskLeoViewport {
  width: number
  height: number
}

/** Narrower than this and the composer plus a message bubble stop fitting. */
export const ASK_LEO_WINDOW_MIN_WIDTH = 320
/** Shorter than this and the transcript shows less than one exchange. */
export const ASK_LEO_WINDOW_MIN_HEIGHT = 360

export const ASK_LEO_WINDOW_DEFAULT_WIDTH = 420
export const ASK_LEO_WINDOW_DEFAULT_HEIGHT = 560

/** 0.5rem — the same inset every DS floating sheet uses. */
export const ASK_LEO_WINDOW_INSET = 8

/** Arrow-key step, and the Shift-held step for crossing the screen faster. */
export const ASK_LEO_WINDOW_NUDGE = 16
export const ASK_LEO_WINDOW_NUDGE_COARSE = 48

export type AskLeoWindowResizeHandle =
  | "n"
  | "s"
  | "e"
  | "w"
  | "ne"
  | "nw"
  | "se"
  | "sw"

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max))
}

/**
 * Opens bottom-right, where the launcher FAB sits, so the window reads as
 * having grown out of the button that was just pressed.
 */
export function defaultAskLeoWindowRect(viewport: AskLeoViewport): AskLeoWindowRect {
  const width = Math.min(
    ASK_LEO_WINDOW_DEFAULT_WIDTH,
    Math.max(ASK_LEO_WINDOW_MIN_WIDTH, viewport.width - ASK_LEO_WINDOW_INSET * 2),
  )
  const height = Math.min(
    ASK_LEO_WINDOW_DEFAULT_HEIGHT,
    Math.max(ASK_LEO_WINDOW_MIN_HEIGHT, viewport.height - ASK_LEO_WINDOW_INSET * 2),
  )
  return clampAskLeoWindowRect(
    {
      x: viewport.width - width - ASK_LEO_WINDOW_INSET,
      y: viewport.height - height - ASK_LEO_WINDOW_INSET,
      width,
      height,
    },
    viewport,
  )
}

/**
 * Keep the whole window inside the viewport inset. Stricter than most desktop
 * window managers, which allow a window to hang off the edge — but this one
 * lives inside a page the user still has to work in, and a half-off-screen
 * window with its resize handles beyond reach is a trap, not a feature.
 */
export function clampAskLeoWindowRect(
  rect: AskLeoWindowRect,
  viewport: AskLeoViewport,
): AskLeoWindowRect {
  const width = clamp(
    rect.width,
    ASK_LEO_WINDOW_MIN_WIDTH,
    Math.max(ASK_LEO_WINDOW_MIN_WIDTH, viewport.width - ASK_LEO_WINDOW_INSET * 2),
  )
  const height = clamp(
    rect.height,
    ASK_LEO_WINDOW_MIN_HEIGHT,
    Math.max(ASK_LEO_WINDOW_MIN_HEIGHT, viewport.height - ASK_LEO_WINDOW_INSET * 2),
  )
  return {
    width,
    height,
    x: clamp(rect.x, ASK_LEO_WINDOW_INSET, viewport.width - width - ASK_LEO_WINDOW_INSET),
    y: clamp(rect.y, ASK_LEO_WINDOW_INSET, viewport.height - height - ASK_LEO_WINDOW_INSET),
  }
}

/** Drag and arrow-key move share this, so both stop at the same edge. */
export function moveAskLeoWindowRect(
  rect: AskLeoWindowRect,
  dx: number,
  dy: number,
  viewport: AskLeoViewport,
): AskLeoWindowRect {
  return clampAskLeoWindowRect({ ...rect, x: rect.x + dx, y: rect.y + dy }, viewport)
}

/**
 * Resize from one of eight handles. `start` is the rect as it was when the
 * gesture began and the deltas are measured from that same instant, so a
 * gesture that drags past a clamp and back returns to where it was instead of
 * accumulating the discarded pixels.
 *
 * North and west edges move the opposite edge's anchor: they change `x` / `y`
 * as well as size, keeping the far edge pinned.
 */
export function resizeAskLeoWindowRect(
  start: AskLeoWindowRect,
  handle: AskLeoWindowResizeHandle,
  dx: number,
  dy: number,
  viewport: AskLeoViewport,
): AskLeoWindowRect {
  let { x, y, width, height } = start

  if (handle.includes("e")) {
    width = clamp(
      start.width + dx,
      ASK_LEO_WINDOW_MIN_WIDTH,
      viewport.width - ASK_LEO_WINDOW_INSET - start.x,
    )
  }
  if (handle.includes("w")) {
    const right = start.x + start.width
    width = clamp(start.width - dx, ASK_LEO_WINDOW_MIN_WIDTH, right - ASK_LEO_WINDOW_INSET)
    x = right - width
  }
  if (handle.includes("s")) {
    height = clamp(
      start.height + dy,
      ASK_LEO_WINDOW_MIN_HEIGHT,
      viewport.height - ASK_LEO_WINDOW_INSET - start.y,
    )
  }
  if (handle.includes("n")) {
    const bottom = start.y + start.height
    height = clamp(
      start.height - dy,
      ASK_LEO_WINDOW_MIN_HEIGHT,
      bottom - ASK_LEO_WINDOW_INSET,
    )
    y = bottom - height
  }

  return clampAskLeoWindowRect({ x, y, width, height }, viewport)
}

/** Cycle order for corner snapping — starts where the launcher FAB lives. */
export const ASK_LEO_WINDOW_CORNERS = [
  "bottom-right",
  "bottom-left",
  "top-left",
  "top-right",
] as const

export type AskLeoWindowCorner = (typeof ASK_LEO_WINDOW_CORNERS)[number]

/** Within a couple of pixels of the inset counts as parked in that corner. */
function cornerOf(
  rect: AskLeoWindowRect,
  viewport: AskLeoViewport,
): AskLeoWindowCorner | null {
  const near = (a: number, b: number) => Math.abs(a - b) < 2
  const left = near(rect.x, ASK_LEO_WINDOW_INSET)
  const top = near(rect.y, ASK_LEO_WINDOW_INSET)
  const right = near(rect.x + rect.width, viewport.width - ASK_LEO_WINDOW_INSET)
  const bottom = near(rect.y + rect.height, viewport.height - ASK_LEO_WINDOW_INSET)
  if (bottom && right) return "bottom-right"
  if (bottom && left) return "bottom-left"
  if (top && left) return "top-left"
  if (top && right) return "top-right"
  return null
}

export function snapAskLeoWindowRect(
  rect: AskLeoWindowRect,
  corner: AskLeoWindowCorner,
  viewport: AskLeoViewport,
): AskLeoWindowRect {
  const right = corner === "bottom-right" || corner === "top-right"
  const bottom = corner === "bottom-right" || corner === "bottom-left"
  return clampAskLeoWindowRect(
    {
      ...rect,
      x: right ? viewport.width - rect.width - ASK_LEO_WINDOW_INSET : ASK_LEO_WINDOW_INSET,
      y: bottom
        ? viewport.height - rect.height - ASK_LEO_WINDOW_INSET
        : ASK_LEO_WINDOW_INSET,
    },
    viewport,
  )
}

/**
 * Park the window in the next corner, keeping its size. Coarse placement in one
 * keystroke or one click — nudging 16px at a time across a 1440px screen is
 * technically parity and practically unusable.
 */
export function snapAskLeoWindowToNextCorner(
  rect: AskLeoWindowRect,
  viewport: AskLeoViewport,
): AskLeoWindowRect {
  const current = cornerOf(rect, viewport)
  const index = current ? ASK_LEO_WINDOW_CORNERS.indexOf(current) : -1
  const next = ASK_LEO_WINDOW_CORNERS[(index + 1) % ASK_LEO_WINDOW_CORNERS.length]
  return snapAskLeoWindowRect(rect, next, viewport)
}

/**
 * Keyboard resize. Grows or shrinks from the bottom-right corner so the
 * window's top-left stays put and the gesture is predictable without a
 * pointer to anchor on.
 */
export function nudgeAskLeoWindowSize(
  rect: AskLeoWindowRect,
  dx: number,
  dy: number,
  viewport: AskLeoViewport,
): AskLeoWindowRect {
  return resizeAskLeoWindowRect(rect, "se", dx, dy, viewport)
}
